/**
 * Copyright (c) 2019, Tidepool Project
 *
 * This program is free software; you can redistribute it and/or modify it under
 * the terms of the associated License, which is identical to the BSD 2-Clause
 * License as published by the Open Source Initiative at opensource.org.
 *
 * This program is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS
 * FOR A PARTICULAR PURPOSE. See the License for more details.
 *
 * You should have received a copy of the License along with this program; if
 * not, you can obtain one from Tidepool Project at tidepool.org.
 */

import * as Yup from 'yup';

/*
 * A set of constants needed in various functions and components
 */

// All regexes below used in Yup validation
export const USPhoneRegex = /^\D?(\d{3})\D?\D?(\d{3})\D?(\d{4})$/;
export const USZipcodeRegex = /^[0-9]{5}(?:-[0-9]{4})?$/;
export const MRNRegex = /^[0-9]{7}$/;
export const TimeRegex = /^(0[0-9]|1[0-9]|2[0-3]|[0-9]):[0-5][0-9]$/;
export const InsulinRegex = /(rapidAdult|rapidChild)/;
export const YearRegex = /^[0-9]{4}$/;

// Validation schema using Yup
// the ".when()" specifies which step the validation is occuring and or if it is neccesary
export const formSchema = Yup.object().shape({
  accountType: Yup.string().required,
  firstName: Yup.string().min(5, 'MORE LETTERS'),
  lastName: Yup.string().when('activeSection', {
    is: 0,
    then: Yup.string().required("Please enter the patient's last name")
  }),
  birthday: Yup.date().when('activeSection', {
    is: 0,
    then: Yup.date().required("Please enter the patient's birthday")
  }),
  mrn: Yup.string().when('activeSection', {
    is: 0,
    then: Yup.string()
    // .matches(MRNRegex, "Must be 7 digits"),
  }),
  email: Yup.string().when('activeSection', {
    is: 0,
    then: Yup.string()
      .email('Invalid Email')
      .required("Please enter the patient's email")
  }),
  yearOfDiagnosis: Yup.string().when('activeSection', {
    is: 0,
    then: Yup.string().matches(YearRegex, 'Year must be a 4 digit number')
  }),
  sex: Yup.string().when('activeSection', {
    is: 0,
    then: Yup.string()
  }),
  weight: Yup.number().when('activeSection', {
    is: 0,
    then: Yup.number().typeError('Weight must be a number')
  }),
  phoneNumber: Yup.string().when('activeSection', {
    is: 0,
    then: Yup.string()
      .matches(USPhoneRegex, 'Phone number must be 10 or 11 digits')
      .required("Please enter the patient's phone number")
  }),
  street1Address: Yup.string().when('activeSection', {
    is: 0,
    then: Yup.string().required("Please enter the patient's address")
  }),
  street2Address: Yup.string().when('activeSection', {
    is: 0,
    then: Yup.string()
  }),
  cityAddress: Yup.string().when('activeSection', {
    is: 0,
    then: Yup.string().required("Please enter the patient's city")
  }),
  stateAddress: Yup.string().when('activeSection', {
    is: 0,
    then: Yup.string().required("Please select the patient's state")
  }),
  zipcodeAddress: Yup.string().when('activeSection', {
    is: 0,
    then: Yup.string()
      .matches(USZipcodeRegex, 'Must be 5 or 9 digits')
      .required("Please enter the patient's zipcode")
  }),
  therapySetting: Yup.string().when('activeSection', {
    is: 1,
    then: Yup.string()
  }),
  loopMode: Yup.string().when('activeSection', {
    is: 1,
    then: Yup.string().required('Please choose an initial pump mode')
  }),
  basalArray: Yup.array().when(['activeSection', 'therapySetting'], {
    is: (step, therapy) => step === 2 && therapy === '',
    then: Yup.array(
      Yup.object().shape({
        id: Yup.string().required('Error with basal array'),
        time: Yup.string()
          .matches(TimeRegex, 'Error with basal array time format')
          .required('Error with basal array'),
        val: Yup.number().required('Error with basal array'),
        isFirst: Yup.boolean()
      })
    ).required('Please input basal rates')
  }),
  bolusArray: Yup.array().when(['activeSection', 'therapySetting'], {
    is: (step, therapy) => step === 2 && therapy === '',
    then: Yup.array(
      Yup.object().shape({
        id: Yup.string().required('Error with bolus array'),
        time: Yup.string()
          .matches(TimeRegex, 'Error with bolus array time format')
          .required('Error with bolus array'),
        val: Yup.number().required('Error with bolus array'),
        isFirst: Yup.boolean()
      })
    ).required('Please input bolus rates')
  }),
  sensitivityArray: Yup.array().when(['activeSection', 'therapySetting'], {
    is: (step, therapy) => step === 2 && therapy === '',
    then: Yup.array(
      Yup.object().shape({
        id: Yup.string().required('Error with sensitivity array'),
        time: Yup.string()
          .matches(TimeRegex, 'Error with sensitivity array time format')
          .required('Error with sensitivity array'),
        val: Yup.number().required('Error with sensitivity array'),
        isFirst: Yup.boolean()
      })
    ).required('Please input sensitivity rates')
  }),
  correctionRange: Yup.array().when(['activeSection', 'therapySetting'], {
    is: (step, therapy) => step === 2 && therapy !== 'cpt',
    then: Yup.array(
      Yup.object().shape({
        id: Yup.string().required('Error with correction range'),
        time: Yup.string()
          .matches(TimeRegex, 'Error with correction range time format')
          .required('Please enter correction range times'),
        lowerLimit: Yup.number().required('Please enter lower limits'),
        upperLimit: Yup.number().required('Please enter upper limits'),
        isFirst: Yup.boolean()
      })
    ).required('Please input correction range values')
  }),
  suspendThreshold: Yup.number().when(['activeSection', 'therapySetting'], {
    is: (step, therapy) => step === 2 && therapy !== 'cpt',
    then: Yup.number().required('Please enter a valid suspend threshold value')
  }),
  insulinModel: Yup.string().when(['activeSection', 'therapySetting'], {
    is: (step, therapy) => step === 2 && therapy !== 'cpt',
    then: Yup.string()
      .matches(InsulinRegex, 'Please select a valid insulin model')
      .required('Please select a valid insulin model'),
    otherwise: Yup.string()
  }),
  maxBasal: Yup.number().when(['activeSection', 'therapySetting', 'loopMode'], {
    is: (step, therapy, mode) => step === 2 && therapy !== 'cpt' && mode === 'clm',
    then: Yup.number().required('Please enter a max basal rate')
  }),
  maxBolus: Yup.number().when(['activeSection', 'therapySetting'], {
    is: (step, therapy) => step === 2 && therapy === '',
    then: Yup.number().required('Please enter a max bolus value')
  }),
  selfStart: Yup.string().when(['activeSection', 'therapySetting'], {
    is: (step, therapy) => step === 2 && therapy !== 'cpt',
    then: Yup.string()
  })
});

// Used for the top bar progress steps at the top of the form
export const ProgressBarStepNames = [
  'Create Patient Account',
  'Complete Patient Profile',
  'Enter Therapy Settings',
  'Review and Send Prescription'
];

// Can largely be ignored, used for setting the "touched" prop to false when switching from first step
export const FirstStepValues = ['therapySetting', 'loopMode'];

// Can largely be ignored, used for setting the "touched" prop to false when switching from second step
export const SecondStepValues = [
  'basalArray',
  'bolusArray',
  'sensitivityArray',
  'correctionRange',
  'suspendThreshold',
  'insulinModel',
  'maxBasal',
  'maxBolus'
];

// Used in the Patient State selector
export const StatesArray = [
  'Alaska',
  'Alabama',
  'Arkansas',
  'American Samoa',
  'Arizona',
  'California',
  'Colorado',
  'Connecticut',
  'District of Columbia',
  'Delaware',
  'Florida',
  'Georgia',
  'Guam',
  'Hawaii',
  'Iowa',
  'Idaho',
  'Illinois',
  'Indiana',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Massachusetts',
  'Maryland',
  'Maine',
  'Michigan',
  'Minnesota',
  'Missouri',
  'Mississippi',
  'Montana',
  'North Carolina',
  'North Dakota',
  'Nebraska',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'Nevada',
  'New York',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Puerto Rico',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Virginia',
  'Virgin Islands',
  'Vermont',
  'Washington',
  'Wisconsin',
  'West Virginia',
  'Wyoming'
];

// Miscellaneous language

export const TherapyLanguage =
  'Provide instructions for initializing the settings for Tidepool Loop for the certified product trainer or to the user.';

export const SelfStartLanguage = name =>
  'You have selected online module start. An order will not be placed for ' +
  name +
  ' to receive in-person training. Instead, they will complete an online course.';

// Help Popup Language

export const TidepoolLoopModePopup = 'Suspend-only mode will not allow basal rates above 100%.';

export const InsulinModelPopup =
  'There are two insulin models to choose from which have different insulin activity ' +
  'curves which will affect the amount of active insulin at any given time. Duration of Insulin Activity for both models is 6 hours. ' +
  '*1: Rapid-Acting - Adults insulin model curve peaks at 75 minutes. *2: Rapid-Acting - Children curve peaks at 65 minutes.';

export const SuspendThresholdPopup =
  'The suspend threshold is a safety feature of the loop algorithm which affects both ' +
  'bolus and basal recommendations by Loop. If any predicted blood glucose is below this threshold, the Loop algorithm will issue a temporary basal rate of 0 U/hr';

export const InsulinSensitivityFactorPopup =
  'The insulin sensitivity factor (ISF) governs the expected drop in blood glucose given one unit of insulin.';

export const ScheduledBasalRatesPopup =
  'The scheduled basal rate refers to the amount of basal insulin the pump is scheduled to deliver throughout the day. ' +
  'These scheduled basal rates are used to counter the rise in blood glucose due to endogenous glucose production.';

export const CarbToInsulinRatioPopup =
  'The carbohydrate-to-insulin ratio governs the amount of insulin needed to cover a given amount of carbohydrates.';

export const MaxBasalPopup =
  'The maximum basal rate is a safety feature of the Loop algorithm, which prevents the Loop algorithm from issuing a temporary basal rate greater than this amount. ';

export const MaxBolusPopup =
  'The maximum bolus parameter governs the maximum amount of bolus insulin that is allowed to be given in any single dose.';

export const CorrectionRangePopup =
  'The correction range is the blood glucose range the Loop algorithm uses to determine corrective actions (e.g., between 90 and 120 mg/dL). ' +
  '*NOTE: Loop’s correction range is a user setting and should not be confused with the target range, typically 70-180 mg/dL, used for the purpose of calculating the percent time in range.';

export const PatientSelfStartPopup =
  'Selecting online module start will not require the patient to have training from a CDE or clinician. This is only recommended for Patients who have DIY Looped' +
  ' and for savvy diabetics.';

export const TermsAndConditions =
  '*TERMS AND CONDITIONS' +
  '*The license granted by said Respondent under Sections 2.1 or 2.2, Contributor must identify itself as the originator of its Contribution, if any, specified by the provisions of this Package in any derivative ' +
  'version prepared by Licensee. Alternately, in lieu of CNRIs License Agreement shall be taken into account in determining the appropriateness of using and distributing the Program by the Licensor. In no event ' +
  "shall Apple's total liability to You a perpetual, worldwide, non-exclusive, no-charge, royalty-free, irrevocable (except as part of a part or the recipients' rights hereunder, except as disclosed pursuant to " +
  'Paragraph 4 and 7 of the Covered Code. However, You may add an explicit geographical distribution limitation excluding those countries, so that distribution is permitted to make sure that you have found elsewhere ' +
  "or that Derived Work without restriction, including without limitation, court costs and reasonable attorneys' fees and expenses. The application of the terms of this Agreement, including one that receives the Program " +
  "shall be distinguished from those in this section do not pertain to any claims or to which You describe recipients' rights hereunder, except as required for reasonable and customary use in describing the origin or " +
  'ownership rights under this Agreement or any Contributor to the computing community at large as a result, the Commercial Distributor in writing of such a program, whether gratis or for combinations of the material terms ' +
  'or conditions of this agreement.' +
  '*If any provision of the possibility of such work; and (b) otherwise make it clear that any Modifications you distribute are governed by the Initial Developer or any portion thereof. Limitations on Patent License. ' +
  'Subject to the community even when you distribute them as separate works.' +
  "*But when you distribute them as separate works. But when you distribute the Original Code and originally made available to Licensee on an 'AS IS' basis. BEOPEN MAKES NO REPRESENTATIONS OR WARRANTIES, EXPRESS " +
  'OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER ' +
  'IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, SHALL THE LICENSOR BE LIABLE TO ANY PERSON FOR ANY INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES OF ANY KIND, EITHER EXPRESS OR IMPLIED WARRANTIES.' +
  '*THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE OR NON-INFRINGING. THE ENTIRE RISK AS TO THE QUALITY AND PERFORMANCE OF THIS AGREEMENT. This LICENSE AGREEMENT FOR PYTHON ' +
  '0.9.0 THROUGH 1.2 -------------------------------------------------- Copyright (c) 1991 - 1995, Stichting Mathematisch Centrum Amsterdam, The Netherlands.' +
  '*All rights reserved. Permission to use, reproduce, modify, display, perform, internally distribute within Your organization, and Externally Deploy Your Modifications and Covered Code, and keep intact ' +
  'all notices in the License.) 3. You may develop, produce, market or distribute. Waiver; Construction. Failure by Apple or any other recipients of Covered Code and the date it initially became available, ' +
  'or at least three years, to give the recipients all the files and the intellectual property rights or ownership rights relating to the terms of this License or a Derived Work may be copied, such that ' +
  'the distribution of the date Contributor first makes Commercial Use of them is covered by this License to Modifications From Contributor. Source Code You distribute. You agree to the original copyright notices in the code they affect.';
